/**
 * Loans the company takes and loans it gives.
 *
 * Three things on this screen are worth knowing, because they are the whole
 * design:
 *
 * **Nothing here is a stored balance.** "As per books" is the posted position;
 * "as of now" adds the interest that has accrued since the last posting. They
 * are shown separately because one ties to the ledger and the other does not
 * yet.
 *
 * **The compliance panel is part of the document.** What the law allows depends
 * on who the counterparty is — a director, a relative, a group company, an
 * outsider — and each check says what would fix it. In warn mode a red check
 * does not stop the work; it is recorded and the approvers see it.
 *
 * **Interest is a monthly act, not a background job.** Somebody runs the
 * accrual, and a month cannot close until they have.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'

interface Option {
  id: string
  name: string
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  vendors: Option[]
  bank_accounts: Option[]
  my_department_id: string | null
  my_location_id: string | null
}

interface Check {
  check_id: string
  status: 'pass' | 'warn' | 'needs_document' | 'blocked'
  message: string
  document_kind: string | null
  citation: string
}

interface Position {
  as_of: string
  principal_outstanding_paise: number
  interest_booked_paise: number
  interest_since_last_accrual_paise: number
  last_accrual_on: string | null
  books_total_paise: number
  now_total_paise: number
  penal_to_date_paise: number
  prepayment_charge_paise: number
  payoff_paise: number
  disbursed_paise: number
  undrawn_paise: number
  overdue_total_paise: number
  overdue_days: number
  overdue_count: number
}

interface Installment {
  id: string
  installment_no: number
  due_on: string
  opening_principal_paise: number
  principal_due_paise: number
  interest_due_paise: number
  fee_due_paise: number
  total_due_paise: number
  closing_principal_paise: number
  paid_paise: number
  status: string
  payment_request_reference: string | null
  expected_receipt_reference: string | null
}

interface Schedule {
  id: string
  version_no: number
  effective_from: string
  reason: string
  rate_bp: number
  status: string
  installments: Installment[]
  total_interest_paise: number
}

interface LoanEvent {
  id: string
  event_type: string
  event_date: string
  principal_paise: number
  interest_paise: number
  penal_paise: number
  fee_paise: number
  tds_paise: number
  cash_paise: number
  period_label: string | null
  status: string
  reason: string | null
  approval_state: string | null
  journal_entry_id: string | null
}

interface Instrument {
  id: string
  reference: string
  family: 'borrowing' | 'lending'
  instrument_type: string
  title: string
  counterparty_party_id: string
  counterparty_name: string
  related_party_type: string
  is_inter_company: boolean
  principal_paise: number
  sanctioned_on: string
  purpose: string | null
  secured: boolean
  interest_type: string
  rate_bp: number
  repayment_structure: string
  tenure_months: number | null
  moratorium_months: number
  first_due_on: string | null
  due_day: number | null
  penal_rate_bp: number
  prepayment_charge_bp: number
  stop_accrual_after_days: number
  mandate_reference: string | null
  status: string
  approval_state: string
  created_by_name: string | null
  closed_on: string | null
  closure_reason: string | null
  default_reason: string | null
  position: Position
  checks: Check[]
  schedule: Schedule | null
  schedule_versions: {
    id: string
    version_no: number
    effective_from: string
    reason: string
    status: string
    rate_bp: number
    installments: number
  }[]
  events: LoanEvent[]
}

interface Summary {
  borrowed_outstanding_paise: number
  lent_outstanding_paise: number
  overdue_paise: number
  overdue_count: number
  income_suspended_count: number
  upcoming: {
    instrument_id: string
    reference: string
    title: string
    family: string
    installment_no: number
    due_on: string
    amount_paise: number
    status: string
  }[]
  interest_by_month: {
    period_label: string
    direction: string
    interest_paise: number
    tds_paise: number
    loans: number
  }[]
  limits: Record<string, number | string>
  accrual_pending: number
}

const KINDS = [
  ['term_loan', 'Term loan'],
  ['demand_loan', 'Demand loan'],
  ['overdraft', 'Overdraft'],
  ['vehicle_or_equipment_loan', 'Vehicle or equipment loan'],
  ['director_loan', "Director's loan"],
  ['relative_loan', 'Loan from a relative'],
  ['shareholder_loan', "Shareholder's loan"],
  ['employee_loan', 'Loan to an employee'],
  ['inter_company_loan', 'Group company loan'],
  ['other_loan', 'Other'],
] as const

const STRUCTURES = [
  ['emi', 'Equal monthly instalments'],
  ['equal_principal', 'Equal principal, interest on the balance'],
  ['bullet', 'Interest now, principal at the end'],
  ['on_demand', 'Repayable on demand — no schedule'],
] as const

const RELATED = [
  ['none', 'No relationship'],
  ['director', 'Director'],
  ['relative_of_director', 'Relative of a director'],
  ['shareholder', 'Shareholder'],
  ['key_managerial_person', 'Key managerial person'],
  ['employee', 'Employee'],
  ['holding_company', 'Holding company'],
  ['subsidiary', 'Subsidiary'],
  ['wholly_owned_subsidiary', 'Wholly owned subsidiary'],
  ['fellow_group_company', 'Fellow group company'],
  ['associate', 'Associate'],
  ['joint_venture', 'Joint venture'],
  ['entity_director_interested', 'Firm a director is interested in'],
  ['financial_institution', 'Bank, NBFC or insurer'],
  ['other_company', 'Another company'],
] as const

const CHECK_TONE: Record<Check['status'], 'ok' | 'warn' | 'error' | 'info'> = {
  pass: 'ok',
  warn: 'warn',
  needs_document: 'warn',
  blocked: 'error',
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

function percent(bp: number): string {
  return `${(bp / 100).toFixed(2).replace(/\.00$/, '')}%`
}

const today = () => new Date().toISOString().slice(0, 10)

export default function Financing({ only }: { only?: 'borrowing' | 'lending' } = {}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [drafting, setDrafting] = useState(false)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['financing'] })
  }

  const rows = useQuery({
    queryKey: ['financing', 'instruments', only ?? 'all'],
    queryFn: () =>
      api.get<Instrument[]>(`/financing/instruments${only ? `?family=${only}` : ''}`),
  })
  const summary = useQuery({
    queryKey: ['financing', 'summary'],
    queryFn: () => api.get<Summary>('/financing/summary'),
  })

  const raiseDue = useMutation({
    mutationFn: () =>
      api.post<{ raised: number; references: string[] }>('/financing/payments/raise-due'),
    onSuccess: refresh,
  })
  const accrue = useMutation({
    mutationFn: (month: string | null) =>
      api.post<{ period_label: string; count: number; interest_paise: number; tds_paise: number }>(
        '/financing/accruals/run',
        { month },
      ),
    onSuccess: refresh,
  })

  if (rows.isLoading) return <Loading what="Loading loans" />
  const listed = rows.data ?? []
  const opened = listed.find((row) => row.id === open)
  const figures = summary.data

  return (
    <>
      <div className="grid">
        <Stat
          label="Borrowed, outstanding"
          value={formatInr(figures?.borrowed_outstanding_paise ?? 0)}
          note="what the company owes"
        />
        <Stat
          label="Lent, outstanding"
          value={formatInr(figures?.lent_outstanding_paise ?? 0)}
          note="what is owed to the company"
        />
        <Stat
          label="Overdue"
          value={formatInr(figures?.overdue_paise ?? 0)}
          note={`${figures?.overdue_count ?? 0} instalment(s) past due`}
        />
        <Stat
          label="Interest to accrue"
          value={figures?.accrual_pending ?? 0}
          note={
            (figures?.accrual_pending ?? 0) > 0
              ? 'this month is not in the books yet'
              : 'this month is accrued'
          }
        />
      </div>

      {figures && (
        <Card title="What the law lets us do" tight>
          <Note>
            Both meters are read from the ledger, so they move as the books do. Borrowing beyond
            capital and free reserves, and lending beyond 60% of the same base, each need the
            members’ approval by special resolution before the money moves.
          </Note>
          <table>
            <tbody>
              <tr>
                <th>Capital and free reserves</th>
                <td className="num">
                  {formatInr(Number(figures.limits.capital_and_free_reserves_paise ?? 0))}
                </td>
                <th>Borrowings</th>
                <td className="num">{formatInr(Number(figures.limits.borrowings_paise ?? 0))}</td>
                <th>Headroom</th>
                <td className="num">
                  {formatInr(Number(figures.limits.borrowing_headroom_paise ?? 0))}
                </td>
              </tr>
              <tr>
                <th>Lending limit (60%)</th>
                <td className="num">{formatInr(Number(figures.limits.s186_limit_paise ?? 0))}</td>
                <th>Loans and investments</th>
                <td className="num">
                  {formatInr(Number(figures.limits.loans_and_investments_paise ?? 0))}
                </td>
                <th>Headroom</th>
                <td className="num">
                  {formatInr(Number(figures.limits.s186_headroom_paise ?? 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      <Card
        title="Falling due"
        hint={`${figures?.upcoming.length ?? 0} in 30 days`}
        tight
        actions={
          <>
            {can('spend.payment_request.create') && (
              <button
                className="small primary"
                disabled={raiseDue.isPending}
                onClick={() => raiseDue.mutate()}
              >
                Raise what is due
              </button>
            )}{' '}
            {can('fin.accrual.post') && (
              <button
                className="small"
                disabled={accrue.isPending}
                onClick={() => accrue.mutate(null)}
              >
                Accrue interest
              </button>
            )}
          </>
        }
      >
        <ErrorNote error={summary.error ?? raiseDue.error ?? accrue.error} />
        {raiseDue.data && (
          <Note kind="ok">
            {raiseDue.data.raised === 0
              ? 'Nothing new was due.'
              : `Raised ${raiseDue.data.raised}: ${raiseDue.data.references.join(', ')}. Payments ` +
                'we owe carry the loan’s approval; collections owed to us are now expected, so ' +
                'the credit can be matched when it arrives.'}
          </Note>
        )}
        {accrue.data && (
          <Note kind="ok">
            {accrue.data.count === 0
              ? `Nothing to accrue for ${accrue.data.period_label} — it is already in the books.`
              : `${accrue.data.period_label}: ${formatInr(accrue.data.interest_paise)} of interest ` +
                `posted on ${accrue.data.count} loan(s)` +
                (accrue.data.tds_paise
                  ? `, with ${formatInr(accrue.data.tds_paise)} of tax withheld.`
                  : '.')}
          </Note>
        )}
        {(figures?.upcoming.length ?? 0) === 0 ? (
          <div className="empty">Nothing falls due in the next 30 days.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Due</th>
                <th>Loan</th>
                <th>Direction</th>
                <th>#</th>
                <th className="num">Amount</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {(figures?.upcoming ?? []).map((item) => (
                <tr key={`${item.instrument_id}-${item.installment_no}`}>
                  <td className="date">{item.due_on}</td>
                  <td>
                    {item.title}
                    <div className="faint mono">{item.reference}</div>
                  </td>
                  <td className="muted">{item.family === 'borrowing' ? 'we pay' : 'we collect'}</td>
                  <td className="muted">{item.installment_no}</td>
                  <td className="num">{formatInr(item.amount_paise)}</td>
                  <td>
                    <Pill value={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title={only === 'lending' ? 'Loans given' : only === 'borrowing' ? 'Loans taken' : 'Loans'}
        hint={`${listed.length}`}
        tight
        actions={
          can('fin.instrument.create') && (
            <button className="small" onClick={() => setDrafting(!drafting)}>
              {drafting ? 'Close' : 'New loan'}
            </button>
          )
        }
      >
        <ErrorNote error={rows.error} />
        {drafting && (
          <DraftForm
            family={only}
            onDone={() => {
              setDrafting(false)
              refresh()
            }}
          />
        )}
        {listed.length === 0 ? (
          <div className="empty">No loans recorded yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>What</th>
                <th>Counterparty</th>
                <th>Rate</th>
                <th className="num">Sanctioned</th>
                <th className="num">Outstanding</th>
                <th className="num">Overdue</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {listed.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.reference}</td>
                  <td>
                    {row.title}
                    <div className="faint">
                      {row.family === 'borrowing' ? 'we owe' : 'owed to us'} ·{' '}
                      {row.instrument_type.replace(/_/g, ' ')}
                    </div>
                  </td>
                  <td>
                    {row.counterparty_name}
                    {row.related_party_type !== 'none' && (
                      <div className="faint">{row.related_party_type.replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="muted">
                    {row.interest_type === 'none' ? 'interest-free' : percent(row.rate_bp)}
                  </td>
                  <td className="num">{formatInr(row.principal_paise)}</td>
                  <td className="num">{formatInr(row.position.principal_outstanding_paise)}</td>
                  <td className="num">
                    {row.position.overdue_total_paise > 0 ? (
                      <>
                        {formatInr(row.position.overdue_total_paise)}
                        <div className="faint">{row.position.overdue_days} days</div>
                      </>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    <Pill value={row.status} />
                    {row.status === 'pending_approval' && (
                      <div className="faint">{row.approval_state}</div>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="small"
                      onClick={() => setOpen(open === row.id ? null : row.id)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {opened && <Detail instrument={opened} onChange={refresh} />}

      {figures && figures.interest_by_month.length > 0 && (
        <Card title="Interest this year" tight>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Direction</th>
                <th className="num">Interest</th>
                <th className="num">Tax withheld</th>
                <th className="num">Loans</th>
              </tr>
            </thead>
            <tbody>
              {figures.interest_by_month.map((item) => (
                <tr key={`${item.period_label}-${item.direction}`}>
                  <td className="mono">{item.period_label}</td>
                  <td className="muted">{item.direction}</td>
                  <td className="num">{formatInr(item.interest_paise)}</td>
                  <td className="num">{formatInr(item.tds_paise)}</td>
                  <td className="num">{item.loans}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// One loan
// ---------------------------------------------------------------------------

function Detail({ instrument, onChange }: { instrument: Instrument; onChange: () => void }) {
  const { can } = useAuth()
  const [reason, setReason] = useState('')
  const [proposing, setProposing] = useState<string | null>(null)
  const row = instrument
  const position = row.position

  const act = useMutation({
    mutationFn: ({ what, body }: { what: string; body?: unknown }) =>
      api.post(`/financing/instruments/${row.id}/${what}`, body ?? {}),
    onSuccess: () => {
      setReason('')
      onChange()
    },
  })
  const postEvent = useMutation({
    mutationFn: (id: string) => api.post(`/financing/events/${id}/post`),
    onSuccess: onChange,
  })
  const submitEvent = useMutation({
    mutationFn: (id: string) => api.post(`/financing/events/${id}/submit`),
    onSuccess: onChange,
  })

  return (
    <Card title={`${row.reference} — ${row.title}`} hint={row.status.replace(/_/g, ' ')}>
      <ErrorNote error={act.error ?? postEvent.error ?? submitEvent.error} />

      <div className="grid">
        <Stat
          label="As per books"
          value={formatInr(position.books_total_paise)}
          note={`${formatInr(position.principal_outstanding_paise)} principal + ${formatInr(
            position.interest_booked_paise,
          )} interest posted`}
        />
        <Stat
          label="As of now"
          value={formatInr(position.now_total_paise)}
          note={`includes ${formatInr(
            position.interest_since_last_accrual_paise,
          )} accrued since ${position.last_accrual_on ?? 'the start'}`}
        />
        <Stat
          label="Payoff today"
          value={formatInr(position.payoff_paise)}
          note={
            position.prepayment_charge_paise
              ? `includes ${formatInr(position.prepayment_charge_paise)} prepayment charge`
              : 'no prepayment charge'
          }
        />
        <Stat
          label="Overdue"
          value={formatInr(position.overdue_total_paise)}
          note={
            position.overdue_total_paise
              ? `${position.overdue_days} days, ${position.overdue_count} instalment(s)`
              : 'nothing late'
          }
        />
      </div>

      <table>
        <tbody>
          <tr>
            <th>Counterparty</th>
            <td>
              {row.counterparty_name}
              {row.related_party_type !== 'none' && ` (${row.related_party_type.replace(/_/g, ' ')})`}
            </td>
            <th>Sanctioned</th>
            <td>
              {formatInr(row.principal_paise)} on {row.sanctioned_on}
            </td>
          </tr>
          <tr>
            <th>Interest</th>
            <td>
              {row.interest_type === 'none' ? 'interest-free' : `${percent(row.rate_bp)} a year`}
              {row.penal_rate_bp > 0 && `, ${percent(row.penal_rate_bp)} penal`}
            </td>
            <th>Repayment</th>
            <td>
              {row.repayment_structure.replace(/_/g, ' ')}
              {row.tenure_months ? `, ${row.tenure_months} months` : ''}
              {row.moratorium_months ? `, after ${row.moratorium_months} months' moratorium` : ''}
            </td>
          </tr>
          <tr>
            <th>Drawn</th>
            <td>
              {formatInr(position.disbursed_paise)}
              {position.undrawn_paise > 0 && `, ${formatInr(position.undrawn_paise)} undrawn`}
            </td>
            <th>Prepared by</th>
            <td>{row.created_by_name ?? '—'}</td>
          </tr>
          {row.mandate_reference && (
            <tr>
              <th>Mandate</th>
              <td className="mono">{row.mandate_reference}</td>
              <th>Auto-debited</th>
              <td>yes</td>
            </tr>
          )}
          {row.default_reason && (
            <tr>
              <th>In default</th>
              <td colSpan={3}>{row.default_reason}</td>
            </tr>
          )}
          {row.closure_reason && (
            <tr>
              <th>Closed</th>
              <td colSpan={3}>
                {row.closed_on} — {row.closure_reason}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <h4>What the law says about this one</h4>
      {row.checks.length === 0 ? (
        <div className="empty">Not checked yet — submit it to run the checks.</div>
      ) : (
        <>
          {row.checks
            .filter((check) => check.status !== 'pass')
            .map((check) => (
              <Note key={check.check_id} kind={CHECK_TONE[check.status]}>
                <strong>{check.check_id.replace(/_/g, ' ')}</strong> — {check.message}
                {check.citation && <div className="faint">{check.citation}</div>}
              </Note>
            ))}
          {row.checks.every((check) => check.status === 'pass') && (
            <Note kind="ok">Every check passes.</Note>
          )}
        </>
      )}

      <div className="row">
        {(row.status === 'draft' || row.status === 'returned') && can('fin.instrument.submit') && (
          <button className="small primary" onClick={() => act.mutate({ what: 'submit' })}>
            Submit for approval
          </button>
        )}
        {row.status === 'approved' &&
          row.family === 'lending' &&
          can('spend.payment_request.create') && (
            <button className="small primary" onClick={() => act.mutate({ what: 'disburse' })}>
              Raise the payment that pays it out
            </button>
          )}
        {row.status === 'approved' &&
          row.family === 'borrowing' &&
          can('spend.payment_request.create') && (
            <button
              className="small primary"
              onClick={() => act.mutate({ what: 'expect-disbursement' })}
            >
              Expect the money
            </button>
          )}
        {(row.status === 'draft' ||
          row.status === 'returned' ||
          row.status === 'approved' ||
          row.status === 'pending_approval') &&
          can('fin.instrument.edit') && (
            <button
              className="small"
              disabled={reason.trim().length < 3}
              onClick={() => act.mutate({ what: 'cancel', body: { reason } })}
            >
              Cancel it
            </button>
          )}
        {(row.status === 'active' || row.status === 'income_suspended') &&
          can('fin.instrument.approve') && (
            <button
              className="small danger"
              disabled={reason.trim().length < 3}
              onClick={() => act.mutate({ what: 'default', body: { reason } })}
            >
              Flag a default
            </button>
          )}
        {row.status === 'defaulted' && can('fin.instrument.approve') && (
          <button className="small" onClick={() => act.mutate({ what: 'clear-default' })}>
            Clear the default
          </button>
        )}
        {can('fin.event.create') && (row.status === 'active' || row.status === 'defaulted') && (
          <>
            <button className="small" onClick={() => setProposing(proposing ? null : 'restructure')}>
              Restructure
            </button>{' '}
            {row.family === 'lending' && (
              <button className="small" onClick={() => setProposing('provision')}>
                Provide for it
              </button>
            )}{' '}
            {row.family === 'lending' && (
              <button className="small" onClick={() => setProposing('write_off')}>
                Write it off
              </button>
            )}{' '}
            {row.family === 'borrowing' && (
              <button className="small" onClick={() => setProposing('waiver')}>
                Record a waiver
              </button>
            )}
          </>
        )}
      </div>

      {(can('fin.instrument.approve') || can('fin.instrument.edit')) && (
        <Field label="Reason" caption="kept on the record, whatever you do next">
          <input value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      )}

      {proposing && (
        <EventForm
          instrument={row}
          kind={proposing}
          onDone={() => {
            setProposing(null)
            onChange()
          }}
        />
      )}

      {row.schedule && (
        <>
          <h4>
            Schedule v{row.schedule.version_no} — {row.schedule.installments.length} instalments,{' '}
            {formatInr(row.schedule.total_interest_paise)} of interest
          </h4>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Due</th>
                <th className="num">Opening</th>
                <th className="num">Principal</th>
                <th className="num">Interest</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {row.schedule.installments.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.installment_no}</td>
                  <td className="date">{item.due_on}</td>
                  <td className="num">{formatInr(item.opening_principal_paise)}</td>
                  <td className="num">{formatInr(item.principal_due_paise)}</td>
                  <td className="num">{formatInr(item.interest_due_paise)}</td>
                  <td className="num">{formatInr(item.total_due_paise)}</td>
                  <td className="num">{formatInr(item.paid_paise)}</td>
                  <td>
                    <Pill value={item.status} />
                    {(item.payment_request_reference || item.expected_receipt_reference) && (
                      <div className="faint mono">
                        {item.payment_request_reference ?? item.expected_receipt_reference}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {row.schedule_versions.length > 1 && (
        <>
          <h4>Earlier schedules</h4>
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>From</th>
                <th>Why</th>
                <th>Rate</th>
                <th className="num">Instalments</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {row.schedule_versions.map((version) => (
                <tr key={version.id}>
                  <td className="muted">v{version.version_no}</td>
                  <td className="date">{version.effective_from}</td>
                  <td className="muted">{version.reason.replace(/_/g, ' ')}</td>
                  <td className="muted">{percent(version.rate_bp)}</td>
                  <td className="num">{version.installments}</td>
                  <td>
                    <Pill value={version.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h4>What has happened</h4>
      {row.events.length === 0 ? (
        <div className="empty">Nothing has moved on this loan yet.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>What</th>
              <th className="num">Principal</th>
              <th className="num">Interest</th>
              <th className="num">Tax</th>
              <th className="num">Cash</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {row.events.map((event) => (
              <tr key={event.id}>
                <td className="date">{event.event_date}</td>
                <td>
                  {event.event_type.replace(/_/g, ' ')}
                  {event.period_label && <div className="faint">{event.period_label}</div>}
                  {event.reason && <div className="faint">{event.reason}</div>}
                </td>
                <td className="num">{formatInr(event.principal_paise)}</td>
                <td className="num">{formatInr(event.interest_paise)}</td>
                <td className="num">{formatInr(event.tds_paise)}</td>
                <td className="num">{formatInr(event.cash_paise)}</td>
                <td>
                  <Pill value={event.status} />
                  {event.approval_state && <div className="faint">{event.approval_state}</div>}
                </td>
                <td className="num">
                  {event.status === 'draft' && can('fin.event.submit') && (
                    <button className="small" onClick={() => submitEvent.mutate(event.id)}>
                      Submit
                    </button>
                  )}
                  {event.status === 'approved' && can('fin.event.create') && (
                    <button className="small primary" onClick={() => postEvent.mutate(event.id)}>
                      Post it
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Proposing a change
// ---------------------------------------------------------------------------

const EVENT_LABEL: Record<string, string> = {
  restructure: 'Restructure the loan',
  provision: 'Provide for a doubtful loan',
  write_off: 'Write the loan off',
  waiver: 'Record what the lender waived',
}

function EventForm({
  instrument,
  kind,
  onDone,
}: {
  instrument: Instrument
  kind: string
  onDone: () => void
}) {
  const [amount, setAmount] = useState('')
  const [interest, setInterest] = useState('')
  const [rate, setRate] = useState((instrument.rate_bp / 100).toString())
  const [tenure, setTenure] = useState(String(instrument.tenure_months ?? 12))
  const [why, setWhy] = useState('')
  const [when, setWhen] = useState(today())

  const create = useMutation({
    mutationFn: () =>
      api.post(`/financing/instruments/${instrument.id}/events`, {
        event_type: kind,
        event_date: when,
        principal_paise: toPaise(amount),
        interest_paise: toPaise(interest),
        reason: why,
        details:
          kind === 'restructure'
            ? { rate_bp: Math.round(Number(rate) * 100), tenure_months: Number(tenure) }
            : {},
      }),
    onSuccess: onDone,
  })

  return (
    <Card title={EVENT_LABEL[kind] ?? kind}>
      <ErrorNote error={create.error} />
      <Note>
        A change to a loan is approved by the people who approved the loan, and posted afterwards.
        The terms are never edited in place, so what was agreed and what changed both stay on the
        record.
      </Note>
      <div className="row">
        <Field label="From">
          <input type="date" value={when} onChange={(event) => setWhen(event.target.value)} />
        </Field>
        {kind === 'restructure' ? (
          <>
            <Field label="New rate" caption="% a year">
              <input value={rate} onChange={(event) => setRate(event.target.value)} />
            </Field>
            <Field label="Months remaining">
              <input value={tenure} onChange={(event) => setTenure(event.target.value)} />
            </Field>
          </>
        ) : (
          <>
            <Field label="Principal">
              <input value={amount} onChange={(event) => setAmount(event.target.value)} />
            </Field>
            {(kind === 'waiver' || kind === 'write_off') && (
              <Field label="Interest" caption="if any was forgiven too">
                <input value={interest} onChange={(event) => setInterest(event.target.value)} />
              </Field>
            )}
          </>
        )}
      </div>
      <Field label="Why" caption="the approver reads this">
        <input value={why} onChange={(event) => setWhy(event.target.value)} />
      </Field>
      <button
        className="primary"
        disabled={create.isPending || why.trim().length < 3}
        onClick={() => create.mutate()}
      >
        Propose it
      </button>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// A new loan
// ---------------------------------------------------------------------------

function DraftForm({
  family,
  onDone,
}: {
  family?: 'borrowing' | 'lending'
  onDone: () => void
}) {
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
  })
  const [form, setForm] = useState({
    family: family ?? 'borrowing',
    instrument_type: family === 'lending' ? 'employee_loan' : 'director_loan',
    title: '',
    counterparty_party_id: '',
    principal: '',
    sanctioned_on: today(),
    interest_free: true,
    rate: '',
    repayment_structure: 'on_demand',
    tenure_months: '12',
    first_due_on: '',
    due_day: '5',
    secured: false,
    purpose: '',
    mandate_reference: '',
    department_id: '',
    location_id: '',
    bank_account_id: '',
  })
  const [profileType, setProfileType] = useState('director')
  const set = (key: string, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }))

  const opts = options.data
  const scheduled = form.repayment_structure !== 'on_demand'

  const saveProfile = useMutation({
    mutationFn: () =>
      api.put(`/parties/${form.counterparty_party_id}/financing-profile`, {
        related_party_type: profileType,
        is_lender: form.family === 'borrowing',
        is_borrower: form.family === 'lending',
      }),
  })
  const create = useMutation({
    mutationFn: async () => {
      // The classification decides which account the loan sits in and which
      // checks apply, so it is saved before the loan is created.
      if (form.counterparty_party_id) await saveProfile.mutateAsync()
      return api.post<Instrument>('/financing/instruments', {
        family: form.family,
        instrument_type: form.instrument_type,
        title: form.title,
        counterparty_party_id: form.counterparty_party_id,
        principal_paise: toPaise(form.principal),
        sanctioned_on: form.sanctioned_on,
        purpose: form.purpose || null,
        secured: form.secured,
        interest_type: form.interest_free ? 'none' : 'fixed',
        rate_bp: form.interest_free ? 0 : Math.round(Number(form.rate || 0) * 100),
        repayment_structure: form.repayment_structure,
        tenure_months: scheduled ? Number(form.tenure_months || 0) : null,
        first_due_on: scheduled ? form.first_due_on || null : null,
        due_day: scheduled && form.due_day ? Number(form.due_day) : null,
        mandate_reference: form.mandate_reference || null,
        department_id: form.department_id || opts?.my_department_id,
        location_id: form.location_id || opts?.my_location_id,
        bank_account_id: form.bank_account_id || null,
      })
    },
    onSuccess: onDone,
  })

  return (
    <Card title="A new loan">
      <ErrorNote error={create.error ?? options.error ?? saveProfile.error} />
      <Note>
        Who the counterparty is decides what the law allows and which account the loan sits in, so
        it is asked first. The checks run when the loan is submitted, and the approvers see what
        they said.
      </Note>
      <div className="row">
        <Field label="Direction">
          <select value={form.family} onChange={(event) => set('family', event.target.value)}>
            <option value="borrowing">The company is borrowing</option>
            <option value="lending">The company is lending</option>
          </select>
        </Field>
        <Field label="Kind">
          <select
            value={form.instrument_type}
            onChange={(event) => set('instrument_type', event.target.value)}
          >
            {KINDS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name it">
          <input value={form.title} onChange={(event) => set('title', event.target.value)} />
        </Field>
      </div>
      <div className="row">
        <Field label="Counterparty">
          <select
            value={form.counterparty_party_id}
            onChange={(event) => set('counterparty_party_id', event.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.vendors ?? []).map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="What they are to us" caption="this decides the rules that apply">
          <select value={profileType} onChange={(event) => setProfileType(event.target.value)}>
            {RELATED.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount">
          <input value={form.principal} onChange={(event) => set('principal', event.target.value)} />
        </Field>
        <Field label="Agreed on">
          <input
            type="date"
            value={form.sanctioned_on}
            onChange={(event) => set('sanctioned_on', event.target.value)}
          />
        </Field>
      </div>
      <div className="row">
        <label style={{ fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.interest_free}
            onChange={(event) => set('interest_free', event.target.checked)}
          />{' '}
          Interest-free
        </label>
        {!form.interest_free && (
          <Field label="Rate" caption="% a year">
            <input value={form.rate} onChange={(event) => set('rate', event.target.value)} />
          </Field>
        )}
        <Field label="Repayment">
          <select
            value={form.repayment_structure}
            onChange={(event) => set('repayment_structure', event.target.value)}
          >
            {STRUCTURES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        {scheduled && (
          <>
            <Field label="Months">
              <input
                value={form.tenure_months}
                onChange={(event) => set('tenure_months', event.target.value)}
              />
            </Field>
            <Field label="First instalment due">
              <input
                type="date"
                value={form.first_due_on}
                onChange={(event) => set('first_due_on', event.target.value)}
              />
            </Field>
            <Field label="Due day" caption="1–28">
              <input value={form.due_day} onChange={(event) => set('due_day', event.target.value)} />
            </Field>
          </>
        )}
      </div>
      <div className="row">
        <Field label="Department">
          <select
            value={form.department_id || opts?.my_department_id || ''}
            onChange={(event) => set('department_id', event.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.departments ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Clinic">
          <select
            value={form.location_id || opts?.my_location_id || ''}
            onChange={(event) => set('location_id', event.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.locations ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Our bank account">
          <select
            value={form.bank_account_id}
            onChange={(event) => set('bank_account_id', event.target.value)}
          >
            <option value="">Choose…</option>
            {(opts?.bank_accounts ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Mandate reference" caption="if the bank debits it automatically">
          <input
            value={form.mandate_reference}
            onChange={(event) => set('mandate_reference', event.target.value)}
          />
        </Field>
        <label style={{ fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.secured}
            onChange={(event) => set('secured', event.target.checked)}
          />{' '}
          Secured
        </label>
      </div>
      <Field label="What it is for" caption="the approvers read this">
        <input value={form.purpose} onChange={(event) => set('purpose', event.target.value)} />
      </Field>
      <button
        className="primary"
        disabled={
          create.isPending ||
          !form.title ||
          !form.counterparty_party_id ||
          !form.principal ||
          (scheduled && !form.first_due_on)
        }
        onClick={() => create.mutate()}
      >
        Save as a draft
      </button>
    </Card>
  )
}
